/* eslint-disable */
/**
 * UNIFIED TALENT CARD COMPONENT
 * Supports two modes: "browse" and "match"
 * - Browse Mode: Visual grid cards for exploration
 * - Match Mode: List cards with fit scores for hiring decisions
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  IconStar,
  IconStarFilled,
  IconMapPin,
  IconClock,
  IconShieldCheck,
  IconBolt,
  IconMessage,
  IconHeart,
  IconHeartFilled,
  IconExternalLink,
  IconCurrencyDollar,
  IconCalendar,
  IconWorld,
  IconCheck,
  IconAward,
  IconUserCheck,
  IconSparkles,
  IconBriefcase,
  IconArrowRight,
} from "../../utils/icon-compat";
import LoomPlayer from "../LoomPlayer";
import { TrustBadgeGroup, TrustScore } from "../trust/TrustBadge";

// ============== FIT SCORE RING ==============
const FitScoreRing = ({ score, size = "default", isEstimated = false }) => {
  const getColor = (score) => {
    if (score >= 90)
      return {
        stroke: "#FF6B2B",
        bg: "bg-brand-soft",
        text: "text-brand",
        label: "Excellent",
      };
    if (score >= 80)
      return {
        stroke: "#FF6B2B",
        bg: "bg-brand-soft",
        text: "text-brand",
        label: "Great",
      };
    if (score >= 70)
      return {
        stroke: "#0F172A",
        bg: "bg-slate-100",
        text: "text-foreground",
        label: "Good",
      };
    if (score >= 60)
      return {
        stroke: "#F59E0B",
        bg: "bg-warning/10",
        text: "text-warning",
        label: "Fair",
      };
    return {
      stroke: "#6B7280",
      bg: "bg-slate-100",
      text: "text-slate-600",
      label: "Low",
    };
  };

  const { stroke, bg, text, label } = getColor(score);

  const sizes = {
    small: { w: 48, r: 18, strokeWidth: 3, fontSize: "text-sm" },
    default: { w: 64, r: 24, strokeWidth: 4, fontSize: "text-lg" },
    large: { w: 72, r: 26, strokeWidth: 5, fontSize: "text-xl" },
  };

  const s = sizes[size] || sizes.default;
  const circumference = 2 * Math.PI * s.r;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div
        className={`relative ${bg} rounded-xl flex items-center justify-center`}
        style={{ width: s.w, height: s.w }}
      >
        <svg
          className="transform -rotate-90"
          style={{ width: s.w, height: s.w }}
        >
          <circle
            cx={s.w / 2}
            cy={s.w / 2}
            r={s.r}
            stroke="#E5E7EB"
            strokeWidth={s.strokeWidth}
            fill="none"
          />
          <circle
            cx={s.w / 2}
            cy={s.w / 2}
            r={s.r}
            stroke={stroke}
            strokeWidth={s.strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className={`${s.fontSize} font-bold ${text}`}>{score}</span>
        </div>
      </div>
      <span className={`text-xs font-medium mt-1 ${text}`}>
        {isEstimated ? "Est. Fit" : label}
      </span>
    </div>
  );
};

// ============== MATCH REASON CHIP ==============
const MatchChip = ({ reason, variant = "default" }) => {
  const variants = {
    default: "bg-emerald-50 text-emerald-700 border-emerald-200",
    skill: "bg-slate-100 text-foreground border-slate-200",
    availability: "bg-brand-soft text-brand border-green-200",
    budget: "bg-slate-100 text-foreground border-purple-200",
    rating: "bg-amber-50 text-amber-700 border-amber-200",
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${variants[variant]}`}
    >
      <IconCheck size={11} strokeWidth={3} />
      {reason}
    </span>
  );
};

const getChipVariant = (reason) => {
  const lower = reason.toLowerCase();
  if (lower.includes("skill")) return "skill";
  if (lower.includes("available") || lower.includes("now"))
    return "availability";
  if (
    lower.includes("budget") ||
    lower.includes("rate") ||
    lower.includes("within")
  )
    return "budget";
  if (lower.includes("rated") || lower.includes("rating")) return "rating";
  return "default";
};

// Trust badges render via the unified TrustBadgeGroup imported above. See
// src/components/trust/TrustBadge.jsx for the canonical signal definitions.

// ============== ROLE FIT CHIP (Browse Mode) ==============
const RoleFitChip = ({ skills = [] }) => {
  const skillNames = skills.map((s) =>
    typeof s === "object" ? s.name || s.skill : s,
  );

  // Determine role suggestion based on skills
  const determineRole = () => {
    const lower = skillNames.map((s) => s.toLowerCase());
    if (
      lower.some(
        (s) =>
          s.includes("react") || s.includes("vue") || s.includes("angular"),
      )
    )
      return "Frontend";
    if (
      lower.some(
        (s) => s.includes("node") || s.includes("python") || s.includes("java"),
      )
    )
      return "Backend";
    if (
      lower.some(
        (s) => s.includes("figma") || s.includes("ui") || s.includes("ux"),
      )
    )
      return "Design";
    if (lower.some((s) => s.includes("seo") || s.includes("marketing")))
      return "Marketing";
    if (lower.some((s) => s.includes("data") || s.includes("analytics")))
      return "Data";
    return "Specialist";
  };

  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-900 text-white rounded-full text-xs font-medium">
      <IconBriefcase size={12} />
      Good fit for {determineRole()} roles
    </span>
  );
};

// Generate match reasons from freelancer data
const generateMatchReasons = (freelancer, criteria = {}) => {
  const reasons = [];
  const {
    skills = [],
    verified,
    vetted,
    topRated,
    rating,
    hourly_rate,
    rate,
    availability,
    responseTime,
    total_jobs,
  } = freelancer;

  // Skills match
  const freelancerSkills = skills.map((s) =>
    (typeof s === "object" ? s.name || s.skill : s).toLowerCase(),
  );
  const criteriaSkills = (criteria.skills || []).map((s) => s.toLowerCase());
  if (criteriaSkills.length > 0) {
    const matched = criteriaSkills.filter((s) => freelancerSkills.includes(s));
    if (matched.length > 0)
      reasons.push(`${matched.length}/${criteriaSkills.length} skills match`);
  }

  // Budget fit
  const displayRate = rate || hourly_rate || 50;
  if (criteria.max_rate && displayRate <= criteria.max_rate) {
    reasons.push("Within budget");
  }

  // Availability
  if (availability === "available" || availability === "full-time") {
    reasons.push("Available now");
  }

  // Trust signals
  if (vetted) reasons.push("Vetted talent");
  else if (verified) reasons.push("Verified");

  // Rating
  const ratingNum = parseFloat(rating) || 0;
  if (ratingNum >= 4.8) reasons.push("Highly rated");
  else if (ratingNum >= 4.5) reasons.push("Great reviews");

  // Response time
  if (
    responseTime &&
    (responseTime.includes("1") || responseTime.includes("<"))
  ) {
    reasons.push("Fast responder");
  }

  // New talent (no jobs yet - don't show misleading 0)
  if (!total_jobs || total_jobs === 0) {
    reasons.push("New talent");
  }

  return reasons.slice(0, 4);
};

// ============== MAIN TALENT CARD COMPONENT ==============
export const TalentVidCard = ({
  mode = "browse", // 'browse' | 'match'
  freelancer,
  fitScore,
  matchReasons = [],
  isEstimated = true,
  isSelected = false,
  isSaved = false,
  searchCriteria = {},
  onSelect,
  onInvite,
  onMessage,
  onSave,
  onViewProfile,
  onPreview,
  onMatchToJob, // Browse mode: opens job selector
  isInvited = false,
  className = "",
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [loomUrl, setLoomUrl] = useState("");

  // Navigate to chat/messages with freelancer context
  const handleMessageClick = (e) => {
    e.stopPropagation();
    const freelancerId = freelancer?.id;
    const freelancerName =
      freelancer?.name || freelancer?.first_name || "Freelancer";
    navigate(
      `/messages?recipient=${freelancerId}&name=${encodeURIComponent(freelancerName)}`,
    );
  };
  const {
    id,
    first_name,
    last_name,
    name,
    title,
    bio,
    avatar_url,
    rate,
    hourly_rate,
    rating,
    reviewCount,
    skills = [],
    location,
    timezone,
    availability,
    responseTime,
    verified = false,
    vetted = false,
    topRated = false,
    total_jobs,
  } = freelancer;

  const displayName =
    name ||
    (first_name ? `${first_name} ${last_name || ""}`.trim() : "Freelancer");
  const displayRate = rate || hourly_rate || 50;
  const displayTitle = title || bio?.slice(0, 50) || "Professional Freelancer";
  const displayReasons =
    matchReasons.length > 0
      ? matchReasons
      : generateMatchReasons(freelancer, searchCriteria);
  const isNewTalent = !total_jobs || total_jobs === 0;

  const getSkillName = (skill) =>
    typeof skill === "object" ? skill.name || skill.skill : skill;

  const handleCardClick = (e) => {
    if (
      e.target.closest("button") ||
      e.target.closest("input") ||
      e.target.closest("a")
    )
      return;
    onPreview?.(freelancer);
  };

  // ============== BROWSE MODE - GRID CARD ==============
  if (mode === "browse") {
    return (
      <>
        <div
          className={`bg-gray-50 rounded-2xl transition-all duration-200 cursor-pointer group shadow border border-gray-100 ${className}`}
          onClick={handleCardClick}
        >
          <div className="p-5">
            {/* Avatar & Name - Visual Emphasis */}
            <div className="flex gap-x-4 items-center mb-6">
              {/* Large Avatar */}
              <div className="relative">
                {avatar_url != null ? (
                  <img
                    src={avatar_url}
                    alt="avatar"
                    className="w-16 h-16 rounded-2xl object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                )}
                {/* Online indicator */}
                {availability === "available" && (
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-brand rounded-full border-3 border-white" />
                )}
              </div>

              <div>
                {/* Name & Title */}
                <h3 className="font-bold text-foreground text-lg group-hover:text-indigo transition-colors">
                  {displayName}
                </h3>
                <p className="text-sm text-slate-500 line-clamp-1">
                  {displayTitle}
                </p>

                {/* Rating */}
                {/* {rating && parseFloat(rating) > 0 && (
              <div className="flex items-center gap-1 mt-1">
                <IconStarFilled size={14} className="text-amber-400" />
                <span className="text-sm font-semibold text-foreground">{parseFloat(rating).toFixed(1)}</span>
              </div>
            )} */}
              </div>
            </div>

            {/* Skills */}
            <div className="flex flex-wrap justify-center gap-1.5 mb-4 overflow-y-hidden h-[25px]">
              {skills.slice(0, 4).map((skill, i) => (
                <span
                  key={i}
                  className="bg-slate-200 text-slate-700 px-2.5 py-1 rounded-full text-xs font-medium"
                >
                  {getSkillName(skill)}
                </span>
              ))}
              {skills.length > 4 && (
                <span className="text-xs text-slate-400">
                  +{skills.length - 4}
                </span>
              )}
            </div>

            <div>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (freelancer.video_url) {
                    setLoomUrl(freelancer.video_url);
                    setIsOpen(true);
                  }
                }}
                rel="noopener noreferrer"
                class="relative w-full max-w-4xl aspect-video rounded-2xl overflow-hidden shadow-2xl group cursor-pointer border border-white/10 block outline-none focus-visible:ring-4 focus-visible:ring-indigo-400"
              >
                <img
                  src={
                    avatar_url != null
                      ? avatar_url
                      : !freelancer?.gender ? 'avatar_male.png' : freelancer?.gender == 'female' ? 'avatar_female.png' : '/avatar-male.png'
                  }
                  alt="avatar"
                  class="absolute inset-0 w-full h-full object-cover brightness-75 group-hover:brightness-90 transition-all duration-300"
                />

                <div class="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"></div>

                <div class="absolute inset-0 flex items-center justify-center">
                  <div
                    class={`w-20 h-20 rounded-full shadow-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-active:scale-95 bg-transparent`}
                  >
                    <svg
                      class={`w-10 h-10 text-white ml-1`}
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M8 5v14l11-7L8 5z" />
                    </svg>
                  </div>
                </div>

                <div class="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent pointer-events-none"></div>

                <span class="absolute bottom-4 left-4 text-white/80 text-sm font-medium tracking-wide bg-black/30 px-3 py-1 rounded-full backdrop-blur-sm">
                  Intro video ›
                </span>
              </a>
            </div>

            {/* Unified Trust Layer */}
            <div className="flex flex-col items-center gap-2 mb-4">
              <TrustScore user={freelancer} />
              <TrustBadgeGroup
                user={freelancer}
                max={3}
                size="xs"
                short
                showLabel={false}
                className="justify-center"
              />
            </div>

            {/* Availability */}
            {availability === "available" && (
              <div className="text-center mb-4">
                <span className="inline-flex items-center gap-1 text-brand text-sm font-medium">
                  <IconClock size={14} />
                  Available now
                </span>
              </div>
            )}

            {/* Actions - Browse Mode CTAs */}
            <div className="space-y-2">
              <Link
                to={`/freelancers/${id}`}
                className="block w-full py-2.5 text-center border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-gray-400 transition-colors"
              >
                View Profile
              </Link>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMatchToJob?.(freelancer);
                }}
                className="w-full py-2.5 bg-brand text-white text-sm font-semibold rounded-lg hover:bg-brand-hover flex items-center justify-center gap-2 transition-colors"
              >
                <IconBriefcase size={16} />
                Match to a Job
              </button>
            </div>
          </div>
        </div>

        {/* Modal Player */}
        <LoomPlayer
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
            setLoomUrl("");
          }}
          loomUrl={loomUrl}
          title={title}
        />
      </>
    );
  }

  // ============== MATCH MODE - LIST CARD ==============
  return (
    <div
      className={`bg-white rounded-xl border-2 transition-all duration-200 cursor-pointer group
        ${
          isSelected
            ? "border-blue-500 shadow-lg shadow-blue-100 ring-2 ring-blue-100"
            : "border-slate-200 hover:border-blue-300 hover:shadow-md"
        } ${className}`}
      onClick={handleCardClick}
    >
      <div className="p-5">
        {/* ============== TOP ROW: Checkbox + Avatar + Info + Fit Score ============== */}
        <div className="flex items-start gap-4">
          {/* Checkbox */}
          <div className="pt-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onSelect?.(freelancer, e.target.checked);
              }}
              className="w-5 h-5 text-indigo rounded border-slate-300 focus:ring-blue-500 cursor-pointer"
            />
          </div>

          {/* Avatar */}
          <div className="relative flex-shrink-0">
            {avatar_url ? (
              <img
                src={avatar_url}
                alt={displayName}
                className="w-14 h-14 rounded-xl object-cover"
              />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
            {availability === "available" && (
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-brand rounded-full border-2 border-white" />
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground truncate text-base group-hover:text-indigo transition-colors">
              {displayName}
            </h3>
            <p className="text-sm text-slate-600 truncate">{displayTitle}</p>

            {rating && parseFloat(rating) > 0 && (
              <div className="flex items-center gap-1.5 mt-1">
                <IconStarFilled size={14} className="text-amber-400" />
                <span className="text-sm font-semibold text-foreground">
                  {parseFloat(rating).toFixed(1)}
                </span>
                {reviewCount > 0 && (
                  <span className="text-xs text-slate-500">
                    ({reviewCount} reviews)
                  </span>
                )}
              </div>
            )}
          </div>

          {/* FIT SCORE - PROMINENT */}
          {(fitScore !== undefined || isInvited) && (
            <div className="flex flex-col items-center gap-2">
              {isInvited && (
                <span className="bg-blue-100 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                  Invited
                </span>
              )}
              {fitScore !== undefined && (
                <FitScoreRing score={fitScore} isEstimated={isEstimated} />
              )}
            </div>
          )}
        </div>

        {/* ============== MATCH REASON CHIPS (Max 4) ============== */}
        <div className="flex flex-wrap gap-1.5 mt-4">
          {displayReasons.slice(0, 4).map((reason, i) => (
            <MatchChip
              key={i}
              reason={reason}
              variant={getChipVariant(reason)}
            />
          ))}
        </div>

        {/* ============== KEY INFO ROW ============== */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4 py-3 border-t border-slate-100">
          <span className="flex items-center gap-1 text-sm">
            <IconCurrencyDollar size={15} className="text-slate-400" />
            <span className="font-semibold text-foreground">
              ${displayRate}
            </span>
            <span className="text-slate-500">/hr</span>
          </span>

          <span className="flex items-center gap-1 text-sm text-slate-600">
            <IconCalendar size={15} className="text-slate-400" />
            {availability === "available" ? (
              <span className="text-brand font-medium">Available now</span>
            ) : (
              <span>Limited</span>
            )}
          </span>

          {timezone && (
            <span className="flex items-center gap-1 text-sm text-slate-600">
              <IconWorld size={15} className="text-slate-400" />
              {timezone}
            </span>
          )}

          {responseTime && (
            <span className="flex items-center gap-1 text-sm text-slate-600">
              <IconClock size={15} className="text-slate-400" />
              <span className="text-brand">{responseTime}</span>
            </span>
          )}
        </div>

        {/* ============== UNIFIED TRUST LAYER ============== */}
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <TrustScore user={freelancer} size="sm" />
          <TrustBadgeGroup user={freelancer} max={4} size="sm" />
        </div>

        {/* ============== ACTIONS ============== */}
        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
          <button
            onClick={(e) => {
              e.stopPropagation();
              !isInvited && onInvite?.(freelancer);
            }}
            disabled={isInvited}
            className={`flex-1 py-2.5 px-4 text-sm font-semibold rounded-lg transition-colors ${
              isInvited
                ? "bg-slate-100 text-slate-500 cursor-not-allowed border border-slate-200"
                : "bg-brand text-white hover:bg-brand-hover active:bg-blue-800"
            }`}
          >
            {isInvited ? (
              <span className="flex items-center justify-center gap-2">
                <IconCheck size={16} /> Invited
              </span>
            ) : (
              "Invite to Job"
            )}
          </button>

          <button
            onClick={handleMessageClick}
            className="p-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-gray-400 transition-colors"
            title="Message"
          >
            <IconMessage size={18} className="text-slate-600" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onSave?.(freelancer);
            }}
            className={`p-2.5 border rounded-lg transition-colors ${
              isSaved
                ? "border-red-200 bg-red-50 hover:bg-red-100"
                : "border-slate-300 hover:bg-slate-50"
            }`}
            title={isSaved ? "Remove from Shortlist" : "Save"}
          >
            {isSaved ? (
              <IconHeartFilled size={18} className="text-red-500" />
            ) : (
              <IconHeart size={18} className="text-slate-600" />
            )}
          </button>

          <Link
            to={`/freelancers/${id}`}
            onClick={(e) => e.stopPropagation()}
            className="p-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 hover:border-gray-400 transition-colors"
            title="View Full Profile"
          >
            <IconExternalLink size={18} className="text-slate-600" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TalentVidCard;
